import React, { useState } from 'react';
import { EXAM_INFO } from '../../data/exam/b2';
import { buildQuestionIndex, formatAnswerValue, flattenQuestions } from './examUtils';

const ExamResultsPage = ({
  skillName,
  scoreData,
  tasks,
  answers,
  resultDetails,
  onRetry,
  onBack,
  onReviewPaper,
}) => {
  const [tab, setTab] = useState('review');
  const percentage = scoreData?.score ?? 0;
  const passed = scoreData?.passed ?? percentage >= EXAM_INFO.passScore;
  const qIndex = buildQuestionIndex(tasks);
  const allQuestions = flattenQuestions(tasks);

  const wrongItems = allQuestions.filter((q) => !resultDetails[q.id]?.is_correct);
  const correctCount = scoreData?.correct ?? 0;
  const total = scoreData?.total ?? allQuestions.length;

  let displayNum = 0;
  const byTeil = {};
  tasks.forEach((task) => {
    byTeil[task.teil] = { task, questions: [] };
    (task.questions || []).forEach((q) => {
      displayNum += 1;
      byTeil[task.teil].questions.push({ ...q, displayNum });
    });
  });

  return (
    <div className="exam-results-root">
      <div className="exam-results-paper">
        <div className="exam-results-header">
          <p className="exam-paper-brand">Goethe-Zertifikat B2</p>
          <p className="exam-paper-subbrand">Auswertung · {skillName}</p>
          <div className={`exam-score-badge ${passed ? 'passed' : 'failed'}`}>
            {percentage}% — {correctCount}/{total}
          </div>
          <p className="text-sm mt-3 font-sans text-brand-text/80">
            {passed ? 'Bestanden' : 'Nicht bestanden'} (Schwelle: {EXAM_INFO.passScore}%)
            {wrongItems.length > 0 && (
              <span className="text-red-700 font-bold"> · {wrongItems.length} falsch</span>
            )}
          </p>
        </div>

        <div className="exam-results-tabs">
          <button
            type="button"
            className={`exam-results-tab ${tab === 'review' ? 'is-active' : ''}`}
            onClick={() => setTab('review')}
          >
            Detaillierte Auswertung
          </button>
          <button
            type="button"
            className={`exam-results-tab ${tab === 'bogen' ? 'is-active' : ''}`}
            onClick={() => setTab('bogen')}
          >
            Antwortbogen
          </button>
        </div>

        {tab === 'bogen' && (
          <div>
            <p className="text-xs px-8 pt-4 pb-2 font-sans text-brand-text/70">
              Grün = richtig · Rot = falsch · Zahl = Ihre Antwort
            </p>
            {Object.values(byTeil).map(({ task, questions }) => (
              <div key={task.id}>
                <div className="exam-review-teil-title">Teil {task.teil}</div>
                <div className="exam-antwortbogen-grid">
                  {questions.map((q) => {
                    const d = resultDetails[q.id];
                    const ok = d?.is_correct;
                    const display = formatAnswerValue(q, answers[q.id]);
                    return (
                      <div
                        key={q.id}
                        className={`exam-antwort-cell ${ok ? 'is-correct' : 'is-wrong'}`}
                        title={q.statement || q.question}
                      >
                        <div className="font-bold">{q.displayNum}</div>
                        <div>{display === '—' ? '·' : display.replace(/\).*/, '').toUpperCase().slice(0, 3)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'review' && (
          <div>
            {wrongItems.length === 0 ? (
              <p className="p-8 text-center font-sans text-green-800 font-bold">
                Alle Antworten richtig!
              </p>
            ) : (
              <p className="text-xs px-8 pt-4 font-sans text-brand-text/70">
                Nur falsche und fehlende Antworten werden hervorgehoben:
              </p>
            )}
            {Object.values(byTeil).map(({ task, questions }) => (
              <div key={task.id}>
                <div className="exam-review-teil-title">Teil {task.teil} — {task.title?.replace(/^Teil \d+ — /, '')}</div>
                {questions.map((q) => {
                  const d = resultDetails[q.id];
                  const ok = d?.is_correct;
                  const meta = qIndex[q.id];
                  return (
                    <div
                      key={q.id}
                      className={`exam-review-item ${ok ? 'is-correct' : 'is-wrong'}`}
                    >
                      <div className="flex justify-between gap-2">
                        <span className="font-bold">Aufgabe {q.displayNum}</span>
                        <span className={`text-xs font-bold uppercase ${ok ? 'text-green-700' : 'text-red-700'}`}>
                          {ok ? 'Richtig' : 'Falsch'}
                        </span>
                      </div>
                      <p className="mt-1 text-brand-text">{meta?.question || meta?.statement}</p>
                      {!ok && (
                        <div className="mt-2 text-sm space-y-0.5">
                          <p>
                            <span className="text-red-800 font-semibold">Ihre Antwort: </span>
                            {formatAnswerValue(q, answers[q.id])}
                          </p>
                          <p>
                            <span className="text-green-800 font-semibold">Richtige Lösung: </span>
                            {formatAnswerValue(q, d?.correct_answer)}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        <div className="exam-submit-bar px-8 pb-8">
          {onReviewPaper && (
            <button type="button" className="exam-btn-secondary" onClick={onReviewPaper}>
              Prüfungsblatt ansehen
            </button>
          )}
          {onRetry && (
            <button type="button" className="exam-btn-primary" onClick={onRetry}>
              Erneut versuchen
            </button>
          )}
          {onBack && (
            <button type="button" className="exam-btn-secondary" onClick={onBack}>
              Alle Module
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExamResultsPage;
